import { ethers } from 'ethers';
import React, { useEffect, useMemo, useState } from 'react';
import Jazzicon, { jsNumberForAddress } from 'react-jazzicon';
import { useAccount } from 'wagmi';
import { CPGABI } from '../../../ABI/ABI';
import {
    CPGAddress,
    DIRECT_PAYMENT,
    initialTokenList,
    targetChainId,
    TRACKED_PAYMENT,
} from '../../../constant/constant';
import {
    findExactMultiple,
    formatNumber,
    shortenWalletAddress,
} from '../../../utils/commonFunction';
import AmountDisplay from '../../../components/amount/AmountDisplay';
import { readContractData } from '../../../utils/contractInstance';
import {
    fetchLockedPaymentRequests,
    fetchPaymentRequests,
} from '../../../utils/cpgDiamondReader';
import { getIncomingPaymentRequestMeta, sortPaymentRequestsNewestFirst } from '../../../utils/paymentRequestNotes';
import ReviewRequestModal from './ReviewRequestModal';

const FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'direct', label: 'Direct' },
    { id: 'tracked', label: 'Tracked' },
];

const RequestCardSkeleton = () => (
    <div className="border-b border-zinc-200 px-4 py-3.5 last:border-b-0 dark:border-slate-800/60">
        <div className="flex animate-pulse items-start gap-3">
            <div className="h-8 w-8 flex-shrink-0 rounded-full bg-zinc-200 dark:bg-slate-800/80" />
            <div className="flex-1 space-y-2">
                <div className="flex items-center justify-between gap-3">
                    <div className="h-3 w-28 rounded bg-zinc-200 dark:bg-slate-800/80" />
                    <div className="h-3 w-20 rounded bg-zinc-100 dark:bg-slate-800/60" />
                </div>
                <div className="h-2.5 w-44 rounded bg-zinc-100 dark:bg-slate-800/50" />
                <div className="flex items-center gap-2">
                    <div className="h-4 w-14 rounded-full bg-zinc-200 dark:bg-slate-800/60" />
                    <div className="h-4 w-20 rounded bg-zinc-100 dark:bg-slate-800/40" />
                </div>
            </div>
        </div>
    </div>
);

const RequestCard = ({ request, onReview }) => {
    const isDirect = request.kind === 'direct';
    const counterparty = request.requester || '';

    return (
        <div
            className="group relative cursor-pointer border-b border-zinc-200 px-4 py-3.5 transition-colors last:border-b-0 hover:bg-zinc-100 dark:border-slate-800/60 dark:hover:bg-slate-900/40"
            onClick={() => onReview(request)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onReview(request);
                }
            }}
        >
            <div className="flex items-start gap-3">
                <div className="flex-shrink-0 overflow-hidden rounded-full ring-1 ring-zinc-300 dark:ring-slate-700/70">
                    {counterparty ? (
                        <Jazzicon diameter={32} seed={jsNumberForAddress(counterparty)} />
                    ) : (
                        <div className="flex h-8 w-8 items-center justify-center bg-zinc-200 dark:bg-slate-800">
                            <i className="fa-solid fa-wallet text-xs text-zinc-500 dark:text-slate-500" />
                        </div>
                    )}
                </div>

                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <p className="truncate text-[13px] font-semibold text-zinc-900 dark:text-slate-200">
                                Wallet
                            </p>
                            <p className="truncate font-mono text-[11px] text-zinc-500 dark:text-slate-500">
                                {shortenWalletAddress(counterparty)}
                            </p>
                        </div>

                        <div className="flex-shrink-0 text-right">
                            <AmountDisplay
                                value={request.parsedNumber ?? request.amountFormatted}
                                className="text-[13px] font-bold text-zinc-900 dark:text-slate-100"
                                suffix={request.tokenName}
                            />
                            {request.kind === 'tracked' && request.milestoneCount ? (
                                <p className="mt-0.5 text-[10px] text-zinc-500 dark:text-slate-500">
                                    {request.milestoneCount} ×{' '}
                                    {request.milestoneIntervalLabel || '—'}
                                </p>
                            ) : null}
                        </div>
                    </div>

                    <div className="mt-2 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 flex-wrap">
                            {isDirect ? (
                                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/[0.07] dark:text-emerald-300/90">
                                    <i className="fa-solid fa-arrow-up-long text-[9px]" />
                                    Direct
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-teal-800 dark:border-teal-500/20 dark:bg-teal-500/[0.07] dark:text-teal-300/90">
                                    <i className="fa-regular fa-clock text-[9px]" />
                                    Tracked
                                    {request.milestoneCount
                                        ? ` · ${request.milestoneCount}ms`
                                        : ''}
                                </span>
                            )}
                            <span className="inline-flex items-center gap-1.5 text-[10px] text-zinc-500 dark:text-slate-500">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-400/80 animate-pulse" />
                                Awaiting approval
                            </span>
                        </div>

                        {/* Review hint shown on hover/focus */}
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-teal-700 opacity-0 transition-opacity group-hover:opacity-100 group-focus:opacity-100 dark:text-emerald-300">
                            <i className="fa-regular fa-eye text-[10px]" />
                            Review
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const IncomingRequestsPanel = () => {
    const { isConnected, address: walletAddress, chainId } = useAccount();
    const [activeFilter, setActiveFilter] = useState('all');
    const [directRequests, setDirectRequests] = useState([]);
    const [trackedRequests, setTrackedRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);

    const decorateDirect = (paymentData, chainIndex) => {
        const tokenInfo = initialTokenList.find(
            (token) =>
                token.address.toLowerCase() ===
                String(paymentData.tokenAddress || '').toLowerCase(),
        );
        if (!tokenInfo) return null;

        const formatted = parseFloat(
            ethers.formatUnits(paymentData.amount, tokenInfo.decimals),
        );
        const requestId = Number(paymentData.requestId);
        const meta = getIncomingPaymentRequestMeta({
            chainId,
            requester: paymentData.requester,
            requestId,
            kind: 'direct',
        });

        return {
            kind: 'direct',
            id: requestId,
            requestId,
            chainIndex,
            requester: paymentData.requester,
            receiver: paymentData.receiver,
            tokenAddress: paymentData.tokenAddress,
            tokenName: tokenInfo.name,
            tokenSymbol: tokenInfo.symbol || tokenInfo.name,
            tokenImage: tokenInfo.logoURI,
            tokenDecimal: tokenInfo.decimals,
            amount: paymentData.amount,
            amountFormatted: formatNumber(formatted),
            parsedNumber: formatted,
            isAccepted: paymentData.isAccepted,
            description: meta.description || '',
        };
    };

    const decorateTracked = (paymentData, chainIndex) => {
        const tokenInfo = initialTokenList.find(
            (token) =>
                token.address.toLowerCase() ===
                String(paymentData.tokenAddress || '').toLowerCase(),
        );
        if (!tokenInfo) return null;

        const formatted = parseFloat(
            ethers.formatUnits(paymentData.amount, tokenInfo.decimals),
        );
        const intervalUnit = findExactMultiple(
            paymentData.milestoneInterval?.toString(),
        );
        const requestId = Number(paymentData.requestId);
        const meta = getIncomingPaymentRequestMeta({
            chainId,
            requester: paymentData.requester,
            requestId,
            kind: 'milestone',
        });

        return {
            kind: 'tracked',
            id: requestId,
            requestId,
            chainIndex,
            requester: paymentData.requester,
            receiver: paymentData.receiver,
            tokenAddress: paymentData.tokenAddress,
            tokenName: tokenInfo.name,
            tokenSymbol: tokenInfo.symbol || tokenInfo.name,
            tokenImage: tokenInfo.logoURI,
            tokenDecimal: tokenInfo.decimals,
            amount: paymentData.amount,
            amountFormatted: formatNumber(formatted),
            parsedNumber: formatted,
            milestoneCount: Number(paymentData.milestones || 0),
            milestoneInterval: paymentData.milestoneInterval?.toString() || null,
            milestoneIntervalLabel: intervalUnit?.unit || null,
            milestones: paymentData.milestones,
            isAccepted: paymentData.isAccepted,
            description: meta.description || '',
        };
    };

    const fetchAllRequests = async () => {
        if (!walletAddress) return;
        try {
            setLoading(true);

            const [directResults, trackedResults] = await Promise.all([
                fetchPaymentRequests(
                    readContractData,
                    CPGAddress,
                    CPGABI,
                    walletAddress,
                    'received',
                ),
                fetchLockedPaymentRequests(
                    readContractData,
                    CPGAddress,
                    CPGABI,
                    walletAddress,
                    'received',
                ),
            ]);

            const directDecorated = directResults
                .map((data) => decorateDirect(data, data.chainIndex))
                .filter(Boolean)
                .filter((r) => !r.isAccepted);
            const trackedDecorated = trackedResults
                .map((data) => decorateTracked(data, data.chainIndex))
                .filter(Boolean)
                .filter((r) => !r.isAccepted);

            setDirectRequests(
                sortPaymentRequestsNewestFirst(directDecorated, { chainId, kind: 'direct' }),
            );
            setTrackedRequests(
                sortPaymentRequestsNewestFirst(trackedDecorated, { chainId, kind: 'milestone' }),
            );
        } catch (error) {
            console.warn(
                'Failed to fetch incoming payment requests:',
                error?.message,
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isConnected && chainId === targetChainId) {
            fetchAllRequests();
        }
        // eslint-disable-next-line
    }, [walletAddress, isConnected, chainId]);

    const visibleRequests = useMemo(() => {
        const merged = sortPaymentRequestsNewestFirst(
            [...directRequests, ...trackedRequests],
            { chainId },
        );
        if (activeFilter === 'direct') return merged.filter((r) => r.kind === 'direct');
        if (activeFilter === 'tracked') return merged.filter((r) => r.kind === 'tracked');
        return merged;
    }, [directRequests, trackedRequests, activeFilter, chainId]);

    const totalCount = directRequests.length + trackedRequests.length;

    return (
        <div className="glass-card flex flex-col overflow-hidden rounded-3xl border border-zinc-200 shadow-xl dark:border-zinc-800">
            {/* Header */}
            <div className="border-b border-zinc-200 px-4 py-4 dark:border-slate-800/60">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h3 className="flex items-center gap-2 text-base font-bold text-zinc-900 dark:text-white">
                            Incoming Requests
                        </h3>
                        <p className="mt-0.5 text-[12px] text-zinc-600 dark:text-slate-400">
                            Payment requests awaiting your approval
                        </p>
                    </div>
                    {totalCount > 0 && (
                        <span className="inline-flex h-6 min-w-[24px] items-center justify-center rounded-full border border-amber-200 bg-amber-50 px-2 text-[11px] font-bold tabular-nums text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
                            {totalCount}
                        </span>
                    )}
                </div>

                {/* Filter tabs */}
                <div className="mt-3 inline-flex w-full rounded-xl border border-zinc-200 bg-zinc-100 p-1 dark:border-slate-800 dark:bg-slate-950/60 sm:w-auto">
                    {FILTERS.map((f) => {
                        const active = activeFilter === f.id;
                        const count =
                            f.id === 'direct'
                                ? directRequests.length
                                : f.id === 'tracked'
                                ? trackedRequests.length
                                : totalCount;

                        return (
                            <button
                                key={f.id}
                                type="button"
                                onClick={() => setActiveFilter(f.id)}
                                className={`flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-[11px] font-semibold transition-all sm:flex-none ${
                                    active
                                        ? 'bg-white text-zinc-900 shadow-sm dark:bg-slate-800 dark:text-white'
                                        : 'text-zinc-600 hover:text-zinc-900 dark:text-slate-400 dark:hover:text-slate-200'
                                }`}
                            >
                                {f.label}
                                {count > 0 && (
                                    <span
                                        className={`tabular-nums text-[10px] ${
                                            active
                                                ? 'text-zinc-600 dark:text-slate-300'
                                                : 'text-zinc-500 dark:text-slate-500'
                                        }`}
                                    >
                                        {count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Body */}
            <div className="max-h-[60vh] overflow-y-auto">
                {loading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                        <RequestCardSkeleton key={i} />
                    ))
                ) : visibleRequests.length === 0 ? (
                    <div className="px-6 py-14 text-center">
                        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-100 dark:bg-slate-800/60">
                            <i className="fa-regular fa-envelope-open text-base text-zinc-500 dark:text-slate-500" />
                        </div>
                        <p className="text-sm font-semibold text-zinc-800 dark:text-slate-300">
                            No pending requests
                        </p>
                        <p className="mx-auto mt-1.5 max-w-xs text-[11px] text-zinc-500 dark:text-slate-500">
                            New {activeFilter !== 'all' ? activeFilter : ''} payment
                            requests will appear here.
                        </p>
                    </div>
                ) : (
                    visibleRequests.map((req) => (
                        <RequestCard
                            key={`${req.kind}-${req.id}`}
                            request={req}
                            onReview={setSelectedRequest}
                        />
                    ))
                )}
            </div>

            {/* Review Modal */}
            {selectedRequest && (
                <ReviewRequestModal
                    request={selectedRequest}
                    selectedPaymentType={
                        selectedRequest.kind === 'direct'
                            ? DIRECT_PAYMENT
                            : TRACKED_PAYMENT
                    }
                    onClose={() => setSelectedRequest(null)}
                    onCompleted={() => {
                        setSelectedRequest(null);
                        fetchAllRequests();
                    }}
                />
            )}
        </div>
    );
};

export default IncomingRequestsPanel;
